import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { endOfYesterday, endOfToday } from 'date-fns'
import Link from 'next/link'
import * as Async from 'contexts/async'
import Head from 'components/Head'
import { Sorting } from 'components/button'
import { Header, Main, Content } from 'components/page'
import { Page } from 'layouts/pages'
import { Muted, Error } from 'components/text'
import { Br } from 'components/misc'
import { Responsive, FlexContentCell } from 'components/table'
import { Metadata, Entry } from 'components/metadata'
import { DropdownFromOptions as Dropdown, DayPicker } from 'components/controls'
import { DateTime, Relative } from 'components/time'
import * as links from 'components/links'
import { NONE, ASC } from '@avcan/constants/sortings'
import { Boundary as ErrorBoundary } from 'components/error'
import { useIntlMemo, useMessageMap } from 'hooks/intl'
import Pagination from 'components/pagination'
import * as Authorization from 'contexts/authorization'
import { useVisibility } from 'hooks/storage/session'
import * as Alerts from 'components/alert'
import Shim from 'components/Shim'
import * as Toaster from 'contexts/toaster'
import * as Alert from 'components/alert'
import * as Button from 'components/button'
import * as Texts from 'components/text'
import * as Context from 'layouts/MountainInformationNetwork/list/context'
import * as Messages from 'constants/min/messages'
import { QUICK } from '@avcan/constants/products/min/types'
import { sorter } from '@avcan/utils/products/min/types'
import pin from 'components/icons/min/min-pin.svg'
import pinWithIncident from 'components/icons/min/min-pin-with-incident.svg'
import styles from 'components/text/Text.module.css'
import { regions } from 'constants/min/regions'

export default function SubmissionListLayout() {
    const intl = useIntl()
    const title = (
        <FormattedMessage
            description="Layout min/SubmissionList"
            defaultMessage="Mountain Information Network — Reports"
        />
    )
    const pageTitle = intl.formatMessage({
        description: 'Mountain Information Network — Submissions',
        defaultMessage: 'Mountain Information Network — Submissions',
    })

    return (
        <Authorization.Provider>
            <Context.Provider>
                <Head title={pageTitle} description={pageTitle} />
                <Toaster.Provider>
                    <Page>
                        <Header title={title} />
                        <Content>
                            <Main>
                                <Toaster.Toaster />
                                <Table />
                            </Main>
                        </Content>
                    </Page>
                </Toaster.Provider>
            </Context.Provider>
        </Authorization.Provider>
    )
}

// Util components
function AdministrationPrivilegesNotice() {
    const [visible, hide] = useVisibility('min-admin')

    if (!visible) {
        return null
    }

    return (
        <Shim vertical>
            <Alerts.Info onDismiss={hide}>
                <FormattedMessage
                    description={'Layout min/SubmissionList'}
                    defaultMessage="You have administrative privileges to edit and delete public MIN posts."
                />
            </Alerts.Info>
        </Shim>
    )
}

function Table() {
    const isAdmin = useCanAdministerMINPosts()

    return (
        <React.Fragment>
            {isAdmin && <AdministrationPrivilegesNotice />}
            <Form />
            <Br />
            <ErrorBoundary fallback={<FallbackError />}>
                <Responsive>
                    <table>
                        <TableHeader />
                        <Async.Found>
                            <TableBody />
                        </Async.Found>
                    </table>
                </Responsive>
                <TableCaption />
            </ErrorBoundary>
        </React.Fragment>
    )
}

function Form() {
    const reportTypeOptions = Messages.useReportTypeOptions()
    const { params, changeFrom, changeTo, changeTypes, changeRegions } = Context.useContext()
    const options = new Map(regions.map(createAreaOption))
    const { from, to, types, region } = params

    return (
        <Metadata>
            <Entry term={<FormattedMessage defaultMessage="From" />} horizontal>
                <DayPicker
                    date={from}
                    onChange={changeFrom}
                    disabledDays={{ after: endOfYesterday() }}
                />
            </Entry>
            <Entry term={<FormattedMessage defaultMessage="To" />} horizontal>
                <DayPicker date={to} onChange={changeTo} disabledDays={{ after: endOfToday() }} />
            </Entry>
            <Entry term={<FormattedMessage defaultMessage="Report Type" />} horizontal>
                <Dropdown
                    value={types}
                    onChange={changeTypes}
                    options={reportTypeOptions}
                    placeholder="Show all"
                />
            </Entry>
            <Entry term={<FormattedMessage defaultMessage="Regions" />} horizontal>
                <Dropdown
                    value={region}
                    onChange={changeRegions}
                    options={options}
                    placeholder={<FormattedMessage defaultMessage="Show all" />}
                />
            </Entry>
        </Metadata>
    )
}
function TableHeader() {
    const columns = useColumns()
    const { params, changeSorting } = Context.useContext()

    function onSortingChange(name, order) {
        switch (name) {
            case 'reporter':
                if (order === ASC) {
                    changeSorting('username')
                } else {
                    changeSorting(null)
                }
                break
            default:
                changeSorting({
                    sorting: name,
                })
                break
        }
    }

    return (
        <thead>
            <tr>
                {columns.map(column => {
                    const { name, title, style, sorting } = column

                    return sorting ? (
                        <FlexContentCell key={name} style={style}>
                            {title}
                            <Sorting
                                sorting={getSorting(name, params.sort)}
                                onChange={order => onSortingChange(name, order)}
                            />
                        </FlexContentCell>
                    ) : (
                        <th key={name} style={style}>
                            {title}
                        </th>
                    )
                })}
            </tr>
        </thead>
    )
}
function TableBody() {
    const submissions = Context.useSubmissions()
    const columns = useColumns()

    return (
        <tbody>
            {submissions.items.data.map(submission => (
                <tr key={submission.id}>
                    {columns.map(({ name, style, property }) => (
                        <td key={name} style={style}>
                            {property(submission)}
                        </td>
                    ))}
                </tr>
            ))}
        </tbody>
    )
}
function TableCaption() {
    const { reset, paramsHaveChanged, submissions = {}, isValidating } = Context.useContext()

    return (
        <Muted>
            {isValidating ? (
                <FormattedMessage
                    description="Layout min/SubmissionList"
                    defaultMessage="Loading reports..."
                />
            ) : (
                <FormattedMessage
                    description="Layout min/SubmissionList"
                    defaultMessage="{count, plural, =0 {No report} one {One report} other {Total of # reports}} found."
                    values={{ count: submissions.itemCount }}
                />
            )}
            {paramsHaveChanged && (
                <p>
                    <FormattedMessage
                        description="Layout min/SubmissionList"
                        defaultMessage="You can <link>clear your search criteria</link> to find more reports."
                        values={{
                            link(chunks) {
                                return (
                                    <Texts.Blue style={{ cursor: 'pointer' }} onClick={reset}>
                                        {chunks}
                                    </Texts.Blue>
                                )
                            },
                        }}
                    />
                </p>
            )}
            <Async.Found>
                <TablePagination />
            </Async.Found>
        </Muted>
    )
}
function FallbackError({ error }) {
    return (
        <React.Fragment>
            <Error>
                <FormattedMessage
                    description="Layout min/SubmissionList"
                    defaultMessage="An error happened while retrieving Mountain Information Network reports."
                />
            </Error>
            <Error>{error.message}</Error>
        </React.Fragment>
    )
}

function TablePagination() {
    const { changePage, submissions } = Context.useContext()
    const { totalPages, page } = submissions

    function movePage(page) {
        changePage(page)
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }

    return <Pagination total={totalPages} active={page} onChange={movePage} />
}

// Hooks

// Utils
function useColumns() {
    const isAdmin = useCanAdministerMINPosts()
    const context = Context.useContext()
    const toaster = Toaster.useContext()

    function deleteReport({ id, title }) {
        toaster.push(<DeleteNotification id={id} title={title} />)
        context.delete(id)
    }

    return useIntlMemo(intl => {
        const columns = [
            {
                name: 'pin',
                property({ id, title, observations }) {
                    const icon = observations.incident > 0 ? pinWithIncident.src : pin.src

                    title = intl.formatMessage(
                        {
                            description: 'Layout min/SubmissionList',
                            defaultMessage: 'Look at {title} report on the map',
                        },
                        { title }
                    )

                    return (
                        <links.MountainInformationNetwork id={id} title={title}>
                            <img src={icon} height={30} width={20} alt="MIN Icon" />
                        </links.MountainInformationNetwork>
                    )
                },
                style: {
                    minWidth: 40,
                },
            },
            {
                name: 'title',
                title: (
                    <FormattedMessage
                        defaultMessage="Title"
                        description="Layout min/SubmissionList"
                    />
                ),
                property({ title, imageCount }) {
                    return (
                        <React.Fragment>
                            {title}
                            <br />
                            {imageCount ? (
                                <small className={styles.Muted}>
                                    <FormattedMessage
                                        description="Layout min/SubmissionList"
                                        defaultMessage="{count, plural, one {# photo} other {# photos}} attached"
                                        values={{ count: imageCount }}
                                    />
                                </small>
                            ) : null}
                        </React.Fragment>
                    )
                },
            },
            {
                name: 'date',
                title: (
                    <FormattedMessage
                        defaultMessage="Date"
                        description="Layout min/SubmissionList"
                    />
                ),
                property({ datetime }) {
                    return (
                        <span>
                            <DateTime value={datetime} />
                            <br />
                            <small className={styles.Muted}>
                                <Relative value={datetime} />
                            </small>
                        </span>
                    )
                },
            },
            {
                name: 'reporter',
                title: (
                    <FormattedMessage
                        defaultMessage="Reporter"
                        description="Layout min/SubmissionList"
                    />
                ),
                property({ username }) {
                    return username
                },
                sorting: NONE,
            },
            {
                name: 'areas',
                title: (
                    <FormattedMessage
                        defaultMessage="Region"
                        description="Layout min/SubmissionList"
                    />
                ),
                property({ region }) {
                    return region
                },
            },
            {
                name: 'observations',
                title: (
                    <FormattedMessage
                        defaultMessage="Available reports"
                        description="Layout min/SubmissionList"
                    />
                ),
                property({ observations, tags }) {
                    const types = Object.entries(observations)
                        .filter(([type, count]) => count > 0)
                        .map(([type]) => type)

                    return <NamesList types={types} tags={tags} />
                },
            },
        ]
        if (isAdmin) {
            columns.push({
                name: 'actions',
                title: (
                    <FormattedMessage
                        defaultMessage="Action"
                        description="Layout min/SubmissionList"
                    />
                ),
                property(report) {
                    return (
                        <div>
                            <Link href={'/account/submissions/' + report.id + '/edit'}>
                                <a>
                                    <FormattedMessage
                                        description="Layout min/SubmissionList"
                                        defaultMessage="Edit"
                                    />
                                </a>
                            </Link>
                            <Texts.Blue
                                style={{ cursor: 'pointer', marginLeft: '1em' }}
                                onClick={() => deleteReport(report)}>
                                <FormattedMessage
                                    description="Layout min/SubmissionList"
                                    defaultMessage="Delete"
                                />
                            </Texts.Blue>
                        </div>
                    )
                },
            })
        }
        return columns
    })
}
function NamesList({ types, tags }) {
    const tagDefinitions = useMessageMap(Messages.QuickReportTags)
    const sorted = [...types].sort(sorter)
    tags = tags.map(tag => tagDefinitions.get(tag)).filter(Boolean)

    return (
        <ul>
            {sorted.map(type => (
                <li key={type}>
                    <FormattedMessage {...Messages.Names[type]} />
                    {type === QUICK && tags.length && ' — ' + tags.join(', ')}
                </li>
            ))}
        </ul>
    )
}
function createAreaOption(area) {
    // TODO: Translate the area name
    return [area, area]
}
// Weird function!!! Used to set the order on the header.
function getSorting(name, sorting) {
    if (name === 'reporter' && sorting === 'username') {
        return ASC
    }

    return NONE
}

function useCanAdministerMINPosts() {
    return Authorization.useHasAuthority('MIN_ADMIN')
}

function DeleteNotification({ id, title }) {
    const toaster = Toaster.useContext()
    const context = Context.useContext()

    function undoDelete() {
        toaster.clear()
        context.undoDelete(id)
    }

    return (
        <Alert.Info>
            <Alert.FlexContent>
                <FormattedMessage
                    description="Layout / MountainInformationNetwork / DeleteNotification"
                    defaultMessage="The report “{title}” has been deleted"
                    values={{
                        title,
                    }}
                />
                <Button.Button onClick={undoDelete}>
                    <FormattedMessage
                        description="Layout / MountainInformationNetwork / DeleteNotification"
                        defaultMessage="Undo"
                    />
                </Button.Button>
            </Alert.FlexContent>
        </Alert.Info>
    )
}
